import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Printer, Mail, ArrowLeft, Download } from 'lucide-react';
import axios from 'axios';
import Notification from './Notification';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuotePreview = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      
      const [quoteRes, settingsRes] = await Promise.all([
        axios.get(`${API}/quotes/${quoteId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setQuote(quoteRes.data);
      setBusinessInfo(settingsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load quote');
      navigate('/');
    }
  };

  useEffect(() => {
    if (quote?.customer_email) {
      setRecipientEmail(quote.customer_email);
    }
  }, [quote]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = async () => {
    if (!quote) return;
    setIsDownloading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/quotes/${quoteId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeNumber = quote.quote_number ? quote.quote_number.replace(/[^a-z0-9-_]+/gi, '_') : 'quote';
      link.href = url;
      link.download = `Quote-${safeNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading quote PDF:', error);
      showNotification('error', 'Failed to download PDF.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleEmail = () => {
    if (!quote || !businessInfo) return;
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    const emailValue = recipientEmail.trim();
    if (!emailValue) {
      showNotification('error', 'Recipient email is required.');
      return;
    }

    setIsSendingEmail(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(
        `${API}/quotes/${quoteId}/email`,
        { recipient_email: emailValue, message: emailMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification('success', `Quote PDF sent to ${emailValue}.`);
      setShowEmailModal(false);
    } catch (error) {
      console.error('Error sending quote email:', error);
      showNotification('error', error.response?.data?.detail || 'Failed to send quote email.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  if (!quote || !businessInfo) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {showEmailModal && (
        <div className="email-modal-backdrop" onClick={() => !isSendingEmail && setShowEmailModal(false)}>
          <div className="email-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Send Quote PDF</h3>
            <p>Email a PDF copy of this quote to your customer.</p>
            <div className="form-group">
              <label className="form-label">Recipient Email</label>
              <input
                type="email"
                className="form-input"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="customer@email.com"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Message (optional)</label>
              <textarea
                className="form-input"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Add a short message for your customer..."
              />
            </div>
            <div className="email-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowEmailModal(false)}
                disabled={isSendingEmail}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSendEmail}
                disabled={isSendingEmail}
              >
                {isSendingEmail ? 'Sending...' : 'Send PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="quote-actions">
        <button onClick={() => navigate('/')} className="btn btn-secondary">
          <ArrowLeft size={20} />
          Back
        </button>
        <button onClick={handlePrint} className="btn btn-primary">
          <Printer size={20} />
          Print Quote
        </button>
        <button onClick={handleDownload} className="btn btn-secondary" disabled={isDownloading}>
          <Download size={20} />
          {isDownloading ? 'Downloading...' : 'Download PDF'}
        </button>
        <button onClick={handleEmail} className="btn btn-success">
          <Mail size={20} />
          Send via Email
        </button>
      </div>

      <div className="quote-preview" id="quote-content">
        <div style={{ borderBottom: '3px solid #4A90E2', paddingBottom: '1.5rem', marginBottom: '2rem' }}>
          {businessInfo.logo_url && (
            <img 
              src={`${BACKEND_URL}${businessInfo.logo_url}`} 
              alt="Business Logo" 
              style={{ maxWidth: '200px', maxHeight: '100px', marginBottom: '1rem' }}
            />
          )}
          <h1 style={{ fontSize: '2.5rem', color: '#333', marginBottom: '0.5rem' }}>
            {businessInfo.business_name}
          </h1>
          <p style={{ fontSize: '1.1rem', color: '#666', margin: '0.25rem 0' }}>
            {businessInfo.business_phone}
          </p>
          <p style={{ fontSize: '1.1rem', color: '#666', margin: '0.25rem 0' }}>
            {businessInfo.business_email}
          </p>
          <p style={{ fontSize: '1.1rem', color: '#666', margin: '0.25rem 0' }}>
            Calgary, Alberta
          </p>
        </div>

        <div style={{ backgroundColor: '#f8f9fa', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '2rem', color: '#333', marginBottom: '1rem' }}>QUOTE</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <p style={{ fontSize: '1rem', color: '#666', marginBottom: '0.25rem' }}>Quote Number</p>
              <p style={{ fontSize: '1.25rem', fontWeight: '600', color: '#333' }}>{quote.quote_number}</p>
            </div>
            <div>
              <p style={{ fontSize: '1rem', color: '#666', marginBottom: '0.25rem' }}>Date</p>
              <p style={{ fontSize: '1.25rem', fontWeight: '600', color: '#333' }}>{quote.date}</p>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.5rem', color: '#333', marginBottom: '1rem' }}>Customer Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: quote.location ? '1fr 200px' : '1fr', gap: '1.5rem' }}>
            <div style={{ padding: '1rem', backgroundColor: '#ffffff', border: '2px solid #e9ecef', borderRadius: '8px' }}>
              <p style={{ fontSize: '1.1rem', color: '#333', marginBottom: '0.5rem' }}>
                <strong>Name:</strong> {quote.customer_name}
              </p>
              {quote.customer_email && (
                <p style={{ fontSize: '1.1rem', color: '#333', marginBottom: '0.5rem' }}>
                  <strong>Email:</strong> {quote.customer_email}
                </p>
              )}
              {quote.customer_address && (
                <p style={{ fontSize: '1.1rem', color: '#333', marginBottom: '0.5rem' }}>
                  <strong>Address:</strong> {quote.customer_address}
                </p>
              )}
              <p style={{ fontSize: '1.1rem', color: '#333', marginBottom: '0.5rem' }}>
                <strong>Phone:</strong> {quote.phone_number}
              </p>
              <p style={{ fontSize: '1.1rem', color: '#333' }}>
                <strong>Service Type:</strong> {quote.service_type}
              </p>
              {quote.location && (
                <p style={{ fontSize: '1.1rem', color: '#333', marginTop: '0.5rem' }}>
                  <strong>Service Location:</strong> {quote.location}
                </p>
              )}
            </div>
            
            {quote.location && (
              <div style={{ 
                border: '3px solid #4A90E2', 
                borderRadius: '8px', 
                overflow: 'hidden',
                height: '200px',
                backgroundColor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem',
                position: 'relative'
              }}>
                {/* Location Pin Icon */}
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style={{ marginBottom: '0.75rem' }}>
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#4A90E2"/>
                </svg>
                
                {/* Location Name */}
                <div style={{
                  color: '#4A90E2',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  marginBottom: '0.25rem'
                }}>
                  {quote.location}
                </div>
                
                {/* Alberta Label */}
                <div style={{
                  color: '#666',
                  fontSize: '0.9rem'
                }}>
                  Alberta, Canada
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.5rem', color: '#333', marginBottom: '1rem' }}>Description of Work</h3>
          <div style={{ padding: '1.5rem', backgroundColor: '#ffffff', border: '2px solid #e9ecef', borderRadius: '8px' }}>
            <p style={{ fontSize: '1.1rem', color: '#333', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>
              {quote.description}
            </p>
          </div>
        </div>

        {quote.notes && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.5rem', color: '#333', marginBottom: '1rem' }}>Additional Notes</h3>
            <div style={{ padding: '1.5rem', backgroundColor: '#ffffff', border: '2px solid #e9ecef', borderRadius: '8px' }}>
              <p style={{ fontSize: '1.1rem', color: '#333', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>
                {quote.notes}
              </p>
            </div>
          </div>
        )}

        <div style={{ backgroundColor: '#4A90E2', color: '#ffffff', padding: '2rem', borderRadius: '8px', textAlign: 'center' }}>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Total Price</h3>
          <p style={{ fontSize: '3rem', fontWeight: 'bold', margin: '0' }}>
            ${parseFloat(quote.price).toFixed(2)} CAD
          </p>
        </div>

        <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '2px solid #e9ecef' }}>
          <p style={{ fontSize: '1rem', color: '#666', textAlign: 'center', lineHeight: '1.6' }}>
            This quote is valid for 30 days from the date issued.<br />
            Thank you for choosing {businessInfo.business_name}!
          </p>
        </div>
      </div>
    </>
  );
};

export default QuotePreview;
